import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsTemplateService } from '@services/dsTemplateService';
import { dsFileService } from '@services/dsFileService';
import { dsReviewService } from '@services/dsReviewService';
import { TemplateBuilder } from '@workpay/ds-shared/components/TemplateBuilder';
import { PdfViewer } from '@workpay/ds-shared/components/PdfViewer';
import { OnlyOfficeEditor } from '@workpay/ds-shared/components/OnlyOfficeEditor';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import SendForReviewModal from '@features/document-reviews/SendForReviewModal';
import { ReviewStatusPanel } from '@workpay/ds-shared/components/ReviewStatusPanel';
import { ChatWidget } from '@workpay/ds-shared/components/ChatWidget';
import { Button } from '@components/ui';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { useNotificationStore } from '@stores/notificationStore';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import { useAuthStore } from '@/stores/authStore';
import { useReviewSse } from '@workpay/ds-shared/hooks/useReviewSse';
import { getLegalEntities } from '@services/legalEntityService';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getDocumentTypes } from '@services/documentTypeService';
import { toTitleCase } from '@utils/textCase';
import { getReviewerBadgeClass } from '@utils/reviewerStatus';
import type { TemplateSigningField } from '@workpay/ds-shared/types/template';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import styles from './TemplateDetail.module.scss';

interface TemplateDetailProps {
  templateId: string;
  routeTo: string;
  initialEdit?: boolean;
}

export function TemplateDetail({ templateId, routeTo, initialEdit = false }: TemplateDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);

  const [isEditing, setIsEditing] = useState(initialEdit);
  const [editedFields, setEditedFields] = useState<TemplateSigningField[]>([]);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [previewMode, setPreviewMode] = useState<'pdf' | 'docx'>('pdf');
  const [docScanning, setDocScanning] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Draggable split
  const [splitPercent, setSplitPercent] = useState(50);
  const [isExpanded, setIsExpanded] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);

  // Escape key exits expanded preview
  useEffect(() => {
    if (!isExpanded) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isExpanded]);

  const handleSplitStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;

    const onMouseMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.max(25, Math.min(75, pct)));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const { data: template, isLoading, error: templateErrorObj } = useQuery({
    queryKey: ['dsTemplate', templateId],
    queryFn: () => dsTemplateService.getById(templateId),
    refetchInterval: 15_000,
  });
  const templateForbidden = isForbiddenError(templateErrorObj);

  // Dynamic breadcrumb: replace static "Template Detail" with actual template name
  useEffect(() => {
    if (template?.name) {
      setOverride(`${routeTo}/${templateId}`, template.name);
      return () => setOverride(`${routeTo}/${templateId}`, null);
    }
  }, [template?.name, templateId, routeTo, setOverride]);

  // Resolve classification IDs to names
  const { countries } = useCountries();
  const countryName = countries.find((c) => c.id === template?.countryId)?.name;

  const { data: legalEntitiesData } = useQuery({
    queryKey: ['legalEntities', { countryId: template?.countryId, size: 100 }],
    queryFn: () => getLegalEntities({ size: 100 }, { filters: { countryId: template!.countryId! } }),
    enabled: !!template?.countryId,
  });
  const legalEntityName = legalEntitiesData?.content?.find((le) => le.id === template?.legalEntityId)?.name;

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });
  const employmentTypeName = employmentTypesData?.content?.find((et) => et.id === template?.employmentTypeId)?.name;

  const { data: documentTypesData } = useQuery({
    queryKey: ['documentTypesAll', { size: 100 }],
    queryFn: () => getDocumentTypes({ size: 100 }),
  });
  const documentTypeName = documentTypesData?.content?.find((dt) => dt.id === template?.documentTypeId)?.name;

  // Blob URL lifecycle is managed via useEffect (not useQuery) because object
  // URLs are external browser resources requiring explicit cleanup. Mirrors
  // the working pattern in the legacy document-signing app.
  const [pdfUrl, setPdfUrl] = useState<string | undefined>();
  const [pdfLoading, setPdfLoading] = useState(false);
  const sourceFileId = template?.sourceFileId;
  const templateFormat = template?.templateFormat;
  useEffect(() => {
    if (!sourceFileId) return;
    let cancelled = false;
    let createdUrl: string | undefined;
    setPdfLoading(true);
    const fetchPromise = templateFormat === 'DOCX'
      ? dsTemplateService.preview(templateId)
      : dsFileService.download(sourceFileId);
    fetchPromise.then((res) => {
      if (!cancelled) {
        createdUrl = URL.createObjectURL(res.data);
        setPdfUrl(createdUrl);
      }
    }).catch(() => {}).finally(() => {
      if (!cancelled) setPdfLoading(false);
    });
    return () => {
      cancelled = true;
      // Defer revoke so pdf.js (inside react-pdf) can finish streaming pages
      // from the blob before the bytes are freed. Revoking synchronously here
      // killed the worker mid-fetch on slower connections and surfaced as
      // "Cannot read properties of null (reading 'sendWithPromise')".
      if (createdUrl) {
        const toRevoke = createdUrl;
        setTimeout(() => URL.revokeObjectURL(toRevoke), 30_000);
      }
    };
  }, [sourceFileId, templateFormat, templateId, previewVersion]);

  const { data: signingFields = [] } = useQuery({
    queryKey: ['dsTemplate-signing-fields', templateId],
    queryFn: () => dsTemplateService.getSigningFields(templateId),
  });

  const { data: placeholders = [] } = useQuery({
    queryKey: ['ds-template-placeholders', templateId],
    queryFn: () => dsTemplateService.getDataPlaceholders(templateId),
  });

  // Reviews
  const { data: reviews = [] } = useQuery({
    queryKey: ['ds-reviews', 'TEMPLATE', templateId],
    queryFn: () => dsReviewService.getForTarget('TEMPLATE', templateId),
    refetchInterval: 10_000,
  });

  useReviewSse({ mode: 'author', targetType: 'TEMPLATE', targetId: templateId, enabled: !isEditing });

  const isInReview = template?.status === 'IN_REVIEW';
  const isDocx = template?.templateFormat === 'DOCX';

  // Exit edit mode AND clear the `?edit=true` URL param so a page refresh
  // lands the user back in view mode instead of re-entering the builder
  // (which would happen because `initialEdit` reads from search params).
  const exitEditMode = useCallback(() => {
    setPreviewVersion((v) => v + 1);
    setIsEditing(false);
    void navigate({
      to: '.',
      search: (prev: { edit?: boolean }) => ({ ...prev, edit: false }),
      replace: true,
    });
  }, [navigate]);

  // Deep-link sanity scrub: if the user landed via `?edit=true` but the
  // template is no longer DRAFT (e.g. stale bookmark, browser back, or another
  // user transitioned status mid-session), reset state and strip the param so
  // a refresh doesn't reopen the same broken state.
  useEffect(() => {
    if (isEditing && template && template.status !== 'DRAFT') {
      exitEditMode();
    }
  }, [isEditing, template, exitEditMode]);

  const deleteMutation = useMutation({
    mutationFn: () => dsTemplateService.delete(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsTemplates'] });
      addNotification({ variant: 'success', title: t('K_DS_NOTIF_TEMPLATE_DELETED', 'Template deleted successfully') });
      navigate({ to: routeTo });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editedFields.length > 0 || signingFields.length === 0) {
        await dsTemplateService.upsertSigningFields(templateId, editedFields);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsTemplate-signing-fields', templateId] });
      queryClient.invalidateQueries({ queryKey: ['ds-template-placeholders', templateId] });
      exitEditMode();
      addNotification({ variant: 'success', title: t('K_DS_NOTIF_FIELDS_SAVED', 'Fields saved successfully') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => dsTemplateService.approve(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsTemplate', templateId] });
      queryClient.invalidateQueries({ queryKey: ['dsTemplates'] });
      addNotification({ variant: 'success', title: t('K_DS_NOTIF_TEMPLATE_ACTIVATED', 'Template activated') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const reviewCancelMutation = useMutation({
    mutationFn: (reviewId: string) => dsReviewService.cancel(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', 'TEMPLATE', templateId] });
      void queryClient.invalidateQueries({ queryKey: ['dsTemplate', templateId] });
      addNotification({ variant: 'success', title: t('K_REVIEW_CANCELLED_SUCCESS', 'Review cancelled successfully.') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const reviewCompleteMutation = useMutation({
    mutationFn: (reviewId: string) => dsReviewService.complete(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', 'TEMPLATE', templateId] });
      void queryClient.invalidateQueries({ queryKey: ['dsTemplate', templateId] });
      addNotification({ variant: 'success', title: t('K_REVIEW_COMPLETED_SUCCESS', 'Review completed successfully.') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const reviewAuthorCompleteMutation = useMutation({
    mutationFn: (reviewId: string) => dsReviewService.markAuthorComplete(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', 'TEMPLATE', templateId] });
      void queryClient.invalidateQueries({ queryKey: ['dsTemplate', templateId] });
      addNotification({ variant: 'success', title: t('K_REVIEW_AUTHOR_COMPLETE_SUCCESS', 'Marked as complete.') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const activeReview = reviews.find((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING') ?? reviews[0];
  const reviewIsActive =
    !!activeReview && (activeReview.status === 'PENDING' || activeReview.status === 'IN_PROGRESS');

  const getTemplateStatusBadgeClass = useCallback((status: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'REVIEW_COMPLETED':
        return styles.badgeSuccess;
      case 'DRAFT':
        return styles.badgeWarning;
      case 'IN_REVIEW':
        return styles.badgeInfo;
      default:
        return styles.badgeNeutral;
    }
  }, []);

  const handleFieldsChange = useCallback((fields: TemplateSigningField[]) => {
    setEditedFields(fields);
  }, []);

  // Enter edit mode AND set `?edit=true` in URL so a refresh keeps the user
  // in builder mode (symmetric with exitEditMode which clears the param).
  const startEditing = useCallback(() => {
    setEditedFields(signingFields);
    setIsEditing(true);
    void navigate({
      to: '.',
      search: (prev: { edit?: boolean }) => ({ ...prev, edit: true }),
      replace: true,
    });
  }, [signingFields, navigate]);

  const handleEditorSave = useCallback(() => {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['ds-template-placeholders', templateId] });
    }, 1500);
  }, [queryClient, templateId]);

  const handlePageCountChange = useCallback((count: number) => {
    if (count > 0 && template && template.pageCount !== count) {
      dsTemplateService.update(templateId, { pageCount: count }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['dsTemplate', templateId] });
      }).catch(() => {
        // Page count update is best-effort — metadata only, does not affect functionality
      });
    }
  }, [template, templateId, queryClient]);

  // --- Forbidden (page-level — detail route can't render anything without the template) ---
  if (templateForbidden) {
    return (
      <div className={styles.page}>
        <Forbidden
          variant="page"
          onAction={() => navigate({ to: routeTo })}
          actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
        />
      </div>
    );
  }

  if (isLoading || !template) {
    return <div className={styles.loading}>{t('K_DS_LOADING_TEMPLATE', 'Loading template...')}</div>;
  }

  // Edit mode — only DRAFT is editable. The action menu buttons already gate
  // on DRAFT, but a stale `?edit=true` deep-link (bookmark, browser back, or
  // a list/detail race where another user transitioned the template into
  // IN_REVIEW) would otherwise mount the builder for a status the backend
  // rejects on save. Fall through to view mode instead.
  if (isEditing && template.status === 'DRAFT') {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.backBtn}
              onClick={exitEditMode}
              disabled={docScanning}
            >
              ← {t('K_DS_CANCEL_EDIT', 'Cancel Edit')}
            </button>
            <h1 className={styles.title}>{template.name}</h1>
            <span className={`${styles.badge} ${styles.badgeWarning}`}>{t('K_DS_EDITING', 'Editing')}</span>
          </div>
          <div className={styles.headerActions}>
            <span className={styles.fieldCount}>{editedFields.length} {t('K_DS_FIELDS', 'fields')}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={exitEditMode}
              disabled={docScanning}
            >
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button
              size="sm"
              loading={saveMutation.isPending}
              onClick={() => {
                // Block save when a DROPDOWN field has no usable options — signers
                // cannot pick from an empty list and Finish Signing stays disabled,
                // blocking the entire envelope. Backend does not enforce this today.
                const invalidDropdowns = editedFields
                  .filter((f) => f.fieldType === 'DROPDOWN')
                  .filter((f) => !(f.dropdownOptions ?? '').split(',').some((o) => o.trim() !== ''))
                  .map((f, i) => f.label || `Dropdown ${i + 1}`);
                if (invalidDropdowns.length > 0) {
                  addNotification({
                    variant: 'critical',
                    title: t(
                      'K_DS_VALIDATION_DROPDOWN_OPTIONS_REQUIRED',
                      'Dropdown fields require at least one option',
                    ),
                    description: invalidDropdowns.join(', '),
                  });
                  return;
                }
                saveMutation.mutate();
              }}
              disabled={docScanning}
            >
              {docScanning ? t('K_DS_SYNCING', 'Syncing...') : t('K_DS_SAVE_CHANGES', 'Save Changes')}
            </Button>
          </div>
        </div>
        <TemplateBuilder
          templateId={templateId}
          templateFormat={template.templateFormat as 'DOCX' | 'PDF'}
          pdfUrl={pdfUrl}
          sourceFileId={template.sourceFileId}
          pageCount={template.pageCount || 1}
          initialFields={signingFields}
          onFieldsChange={handleFieldsChange}
          onPageCountChange={handlePageCountChange}
          onAutoScanStateChange={setDocScanning}
        />
      </div>
    );
  }

  // View mode — split layout with draggable handle
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate({ to: routeTo })}>
            ← {t('K_DS_TEMPLATES', 'Templates')}
          </button>
          <h1 className={styles.title} title={template.name}>
            {template.name}
          </h1>
          <span className={`${styles.badge} ${getTemplateStatusBadgeClass(template.status)}`}>
            {template.status === 'IN_REVIEW'
              ? t('K_DS_STATUS_IN_REVIEW', 'In Review')
              : template.status === 'REVIEW_COMPLETED'
                ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')
                : toTitleCase(template.status)}
          </span>
          <span className={`${styles.badge} ${isDocx ? styles.badgeInfo : styles.badgeNeutral}`}>
            {template.templateFormat}
          </span>
        </div>
        <div className={styles.headerActions}>
          {isInReview && (
            <ReviewStatusPanel
              targetType="TEMPLATE"
              targetId={templateId}
              compact
              hideActions
              onStatusChange={() => {
                void queryClient.invalidateQueries({ queryKey: ['dsTemplate', templateId] });
              }}
            />
          )}
          <ProfileActionsMenu
            actions={(() => {
              const items: ProfileAction[] = [];

              const isDraftLike =
                template.status === 'DRAFT' || template.status === 'REVIEW_COMPLETED';

              if (isDraftLike && !isInReview && hasPermission('CONTRACT_TEMPLATES_APPROVE')) {
                items.push({
                  id: 'activate',
                  label: t('K_DS_ACTIVATE', 'Activate'),
                  variant: 'primary',
                  onClick: () => activateMutation.mutate(),
                });
              }
              // Edit Fields is locked to DRAFT — once a review has completed
              // the field set is "approved" and the only forward action is
              // Activate (or Delete to start over).
              if (template.status === 'DRAFT' && hasPermission('CONTRACT_TEMPLATES_UPDATE')) {
                items.push({
                  id: 'edit-fields',
                  label: t('K_DS_EDIT_FIELDS', 'Edit Fields'),
                  onClick: startEditing,
                });
              }
              // Send for Review intentionally locked to DRAFT only — once a
              // review has completed, the next action is Activate, not another
              // round of review.
              if (template.status === 'DRAFT' && isDocx && hasPermission('CONTRACT_TEMPLATES_UPDATE')) {
                items.push({
                  id: 'send-for-review',
                  label: t('K_DS_SEND_FOR_REVIEW', 'Send for Review'),
                  onClick: () => setShowReviewModal(true),
                });
              }

              if (isInReview && reviewIsActive && activeReview) {
                if (!activeReview.authorCompleted) {
                  items.push({
                    id: 'mark-review-complete',
                    label: t('K_BUTTON_MARK_COMPLETE', 'Mark as Complete'),
                    variant: 'primary',
                    onClick: () => reviewAuthorCompleteMutation.mutate(activeReview.id),
                  });
                }
                items.push({
                  id: 'force-complete-review',
                  label: t('K_BUTTON_FORCE_COMPLETE', 'Force Complete'),
                  onClick: () => reviewCompleteMutation.mutate(activeReview.id),
                });
                items.push({
                  id: 'cancel-review',
                  label: t('K_BUTTON_CANCEL_REVIEW', 'Cancel Review'),
                  onClick: () => reviewCancelMutation.mutate(activeReview.id),
                });
              }

              if (template.status === 'ACTIVE' && hasPermission('CONTRACT_TEMPLATES_VIEW')) {
                items.push({
                  id: 'view-tenant-snapshots',
                  label: t('K_DS_VIEW_TENANT_SNAPSHOTS', 'View Client Snapshots'),
                  onClick: () => navigate({
                    to: '/w/contracts',
                    search: { tab: 'ALL', filters: JSON.stringify({ parentTemplateId: templateId }) },
                  }),
                });
              }

              if (!isInReview && hasPermission('CONTRACT_TEMPLATES_DELETE')) {
                items.push({
                  id: 'delete',
                  label: t('K_DELETE', 'Delete'),
                  onClick: () => setShowDeleteModal(true),
                });
              }

              if (hasPermission('AUDIT_READ')) {
                items.push({
                  id: 'audit',
                  label: t('K_AUDIT_TITLE', 'Audit trail'),
                  onClick: () => setAuditOpen(true),
                });
              }

              return items;
            })()}
          />
        </div>
      </div>

      <div className={styles.threeColumn} ref={splitContainerRef}>
        {/* Left panel: template details */}
        <div className={styles.leftPanel} style={{ width: `${splitPercent}%` }}>
          <div className={styles.leftPanelScroll}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('K_DS_DETAILS', 'Details')}</h2>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_NAME', 'Name')}</span>
                  <span className={styles.detailValue}>{template.name}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_DESCRIPTION', 'Description')}</span>
                  <span className={styles.detailValue}>{template.description || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_COUNTRY', 'Country')}</span>
                  <span className={styles.detailValue}>{countryName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_LEGAL_ENTITY', 'Legal Entity')}</span>
                  <span className={styles.detailValue}>{legalEntityName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}</span>
                  <span className={styles.detailValue}>{employmentTypeName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_DOCUMENT_TYPE', 'Document Type')}</span>
                  <span className={styles.detailValue}>{documentTypeName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_FORMAT', 'Format')}</span>
                  <span className={styles.detailValue}>{template.templateFormat}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_COLUMN_STATUS', 'Status')}</span>
                  <span className={styles.detailValue}>{toTitleCase(template.status)}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_COLUMN_PAGES', 'Pages')}</span>
                  <span className={styles.detailValue}>{template.pageCount || 'N/A'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_COLUMN_CREATED', 'Created')}</span>
                  <span className={styles.detailValue}>{new Date(template.createdOn).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Reviewers list */}
            {reviews.some(r => r.reviewers?.length > 0) && (() => {
              const withReviewers = reviews.filter(r => r.reviewers?.length > 0).slice().reverse();
              const showLabels = withReviewers.length > 1;
              return (
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>{t('K_DS_REVIEWERS', 'Reviewers')}</h2>
                  {withReviewers.map((review, idx) => (
                    <div key={review.id}>
                      {showLabels && (
                        <div className={styles.reviewCycleLabel}>
                          {review.status === 'IN_PROGRESS'
                            ? `${t('K_DS_REVIEW', 'Review')} ${idx + 1} (${t('K_DS_CURRENT', 'Current')})`
                            : `${t('K_DS_REVIEW', 'Review')} ${idx + 1}`}
                          {review.status !== 'IN_PROGRESS' && (
                            <span className={`${styles.badge} ${review.status === 'COMPLETED' ? styles.badgeSuccess : styles.badgeNeutral}`}>
                              {toTitleCase(review.status)}
                            </span>
                          )}
                        </div>
                      )}
                      {review.reviewers.map((r) => (
                        <div key={r.id} className={styles.reviewerCard}>
                          <div className={styles.reviewerInfo}>
                            <span className={styles.reviewerName}>{r.name}</span>
                            <span className={styles.reviewerEmail}>{r.email}</span>
                          </div>
                          <span className={`${styles.badge} ${getReviewerBadgeClass(r.status, styles)}`}>
                            {toTitleCase(r.status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  {t('K_DS_SIGNING_FIELDS', 'Signing Fields')} ({signingFields.length})
                </h2>
                {template.status === 'DRAFT' && hasPermission('CONTRACT_TEMPLATES_UPDATE') && (
                  <Button variant="ghost" size="sm" onClick={startEditing}>
                    {t('K_EDIT', 'Edit')}
                  </Button>
                )}
              </div>
              {signingFields.length === 0 ? (
                <div className={styles.empty}>
                  <p>{t('K_DS_NO_SIGNING_FIELDS', 'No signing fields configured.')}</p>
                  {template.status === 'DRAFT' && hasPermission('CONTRACT_TEMPLATES_UPDATE') && (
                    <Button variant="outline" size="sm" onClick={startEditing}>
                      {t('K_DS_ADD_FIELDS', 'Add Fields')}
                    </Button>
                  )}
                </div>
              ) : (
                <div className={styles.fieldList}>
                  {signingFields.map((f) => (
                    <div key={f.id} className={styles.fieldCard}>
                      <div className={styles.fieldCardTop}>
                        <span className={styles.fieldType}>{f.fieldType}</span>
                        <span className={styles.fieldLabel}>{f.label || '—'}</span>
                        {f.isReadOnly && <span className={`${styles.badge} ${styles.badgeDanger}`}>{t('K_DS_LOCKED', 'Locked')}</span>}
                      </div>
                      <span className={styles.fieldMeta}>
                        {t('K_DS_PAGE', 'Page')} {f.pageNumber} · {t('K_DS_SIGNER', 'Signer')} {f.recipientIndex}
                        {f.isRequired ? ` · ${t('K_DS_REQUIRED', 'Required')}` : ''}
                      </span>
                      {f.defaultValue && (
                        <span className={styles.fieldDefault}>
                          {t('K_DS_DEFAULT', 'Default')}: {f.fieldType === 'CHECKBOX' ? (f.defaultValue === 'true' ? 'Checked' : 'Unchecked') : f.defaultValue}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isDocx && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  {t('K_DS_DATA_PLACEHOLDERS', 'Data Placeholders')} ({placeholders.length})
                </h2>
                {placeholders.length === 0 ? (
                  <p className={styles.emptyText}>{t('K_DS_NO_DATA_PLACEHOLDERS', 'No data placeholders configured.')}</p>
                ) : (
                  <div className={styles.fieldList}>
                    {placeholders.map((p) => (
                      <div key={p.placeholderKey} className={styles.fieldCard}>
                        <div className={styles.fieldCardTop}>
                          <span className={styles.fieldType}>{p.fieldType || 'TEXT'}</span>
                          <span className={styles.fieldLabel} style={{ fontFamily: 'monospace' }}>
                            {`{{${p.placeholderKey}}}`}
                          </span>
                        </div>
                        <span className={styles.fieldMeta}>
                          {p.dataSourceType}{p.dataSourceField ? ` → ${p.dataSourceField}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
          {reviews.length > 0 && (
            <ChatWidget
              targetType="TEMPLATE"
              targetId={templateId}
              reviews={reviews}
              mode="author"
              positioning="absolute"
            />
          )}
        </div>

        {/* Draggable split handle */}
        <div className={styles.splitHandle} onMouseDown={handleSplitStart} />

        {/* Right panel: editor/preview */}
        <div className={`${styles.rightPanel} ${isExpanded ? styles.rightPanelExpanded : ''}`}>
          <div className={styles.previewToolbar}>
            <span className={styles.previewToolbarTitle}>{template.name}</span>
            <span className={styles.previewToolbarMeta}>
              {template.pageCount} {template.pageCount === 1
                ? t('K_DS_PAGE', 'page')
                : t('K_DS_PAGES', 'pages')}
            </span>
            <button
              className={styles.expandBtn}
              onClick={() => setIsExpanded((v) => !v)}
              title={isExpanded ? t('K_DS_EXIT_FULLSCREEN', 'Exit fullscreen') : t('K_DS_FULLSCREEN', 'Fullscreen')}
            >
              {isExpanded ? '⊡' : '⊞'}
            </button>
          </div>
          {isDocx && !isInReview && (
            <div className={styles.previewToggle}>
              <button
                className={`${styles.previewToggleBtn} ${previewMode === 'pdf' ? styles.active : ''}`}
                onClick={() => {
                  setPreviewMode('pdf');
                  setPreviewVersion(v => v + 1);
                }}
              >
                {t('K_DS_PDF_PREVIEW', 'PDF Preview')} ({signingFields.length} {t('K_DS_FIELDS', 'fields')})
              </button>
              <button
                className={`${styles.previewToggleBtn} ${previewMode === 'docx' ? styles.active : ''}`}
                onClick={() => setPreviewMode('docx')}
              >
                {t('K_DS_DOCUMENT', 'Document')} ({placeholders.length} {t('K_DS_PLACEHOLDERS', 'placeholders')})
              </button>
            </div>
          )}
          <div className={styles.previewContent}>
            {isInReview && isDocx && template.sourceFileId ? (
              <OnlyOfficeEditor
                fileId={template.sourceFileId}
                mode="review-author"
                onSave={handleEditorSave}
              />
            ) : isDocx && previewMode === 'docx' && template.sourceFileId ? (
              <OnlyOfficeEditor
                fileId={template.sourceFileId}
                mode="view"
                onSave={handleEditorSave}
              />
            ) : pdfLoading ? (
              <div className={styles.pdfLoader}>
                <div className={styles.spinner} />
                {t('K_DS_GENERATING_PREVIEW', 'Generating PDF preview...')}
              </div>
            ) : pdfUrl ? (
              <div className={styles.pdfViewerWrap}>
                <PdfViewer
                  url={pdfUrl}
                  pageNumber={1}
                  fields={signingFields}
                  allFields={signingFields}
                  selectedFieldIndex={null}
                  onFieldSelect={() => {}}
                  onFieldMove={() => {}}
                  zoom={1}
                  onDocumentLoad={() => {}}
                  onVisiblePageChange={() => {}}
                />
              </div>
            ) : (
              <div className={styles.pdfLoader}>
                {t('K_DS_LOADING_PREVIEW', 'Loading preview...')}
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertModal
        variant="danger"
        title={t('K_DS_MODAL_DELETE_TEMPLATE', 'Delete Template')}
        description={t('K_DS_CONFIRM_DELETE_TEMPLATE', 'Are you sure you want to delete "{name}"?').replace('{name}', template.name)}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        primaryLabel={t('K_DELETE', 'Delete')}
        onPrimaryClick={() => deleteMutation.mutate()}
        loading={deleteMutation.isPending}
      />

      {showReviewModal && (
        <SendForReviewModal
          targetType="TEMPLATE"
          targetId={templateId}
          onClose={() => setShowReviewModal(false)}
          onSuccess={() => {
            setShowReviewModal(false);
            queryClient.invalidateQueries({ queryKey: ['dsTemplate', templateId] });
          }}
        />
      )}

      <AuditTrailModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        filter={{ resourceType: AUDIT_RESOURCE.DOCUMENT_TEMPLATE, resourceId: templateId }}
        subject={template.name || undefined}
      />

    </div>
  );
}
